import { useQuery } from "@tanstack/react-query";
import dayjs from "@/lib/bizday";
import api from "@/lib/api";

const NO_STAFF_TIME = ["boarding", "daycare"];

// Lay out service blocks like the backend (sequential from booking start; stays don't reserve staff).
export function serviceLines(selectedPets, rowsFor, svcById, petName) {
  return selectedPets.flatMap((pid) => rowsFor(pid).map((r) => {
    const s = svcById(r.service_id) || {};
    return { key: `${pid}:${r.service_id}`, pet: petName(pid), service_id: r.service_id, name: s.name || "Service",
      category: (s.category || "").toLowerCase(), duration: (Number(s.duration) || 60) * (Number(r.qty) || 1) };
  })).filter((l) => !NO_STAFF_TIME.includes(l.category));
}

function Row({ line, start, branchId, value, onChange }) {
  const end = dayjs(start).add(line.duration, "minute");
  const ok = dayjs(start).isValid();
  // same fixed business offset the booking submit uses (+05:30), independent of the browser timezone
  const s = encodeURIComponent(`${dayjs(start).format("YYYY-MM-DDTHH:mm:ss")}+05:30`);
  const e = encodeURIComponent(`${end.format("YYYY-MM-DDTHH:mm:ss")}+05:30`);
  const { data, isLoading } = useQuery({ queryKey: ["staff-avail-svc", line.service_id, s, e, branchId], enabled: ok,
    queryFn: async () => (await api.get(`/staff/availability?start=${s}&end=${e}&service_id=${line.service_id}&category=${line.category}&only_eligible=true&branch_id=${branchId || ""}`)).data });
  const items = data?.items || [];
  const cur = items.find((x) => x.id === value);
  return (
    <div className="min-w-0 rounded-lg border border-slate-200 bg-white p-2.5" data-testid={`bk-svc-staff-row-${line.key}`}>
      <div className="flex flex-wrap items-baseline justify-between gap-x-2">
        <span className="min-w-0 break-words text-sm font-semibold text-slate-800">{line.name}{line.pet && <span className="font-normal text-slate-500"> · {line.pet}</span>}</span>
        <span className="text-xs text-slate-500">{ok ? `${dayjs(start).format("D MMM, h:mm A")} – ${end.format("h:mm A")}` : "Pick date & time"} · {line.duration} min</span>
      </div>
      <select value={value || ""} onChange={(ev) => onChange(ev.target.value)} className="mt-1.5 h-10 w-full rounded-md border border-slate-200 bg-white px-2 text-sm" data-testid={`bk-svc-staff-${line.key}`}>
        <option value="">Unassigned</option>
        {items.map((x) => <option key={x.id} value={x.id}>{x.name} ({x.role}){x.available ? "" : x.unavailable ? " — unavailable" : " — busy"}</option>)}
      </select>
      {!isLoading && ok && !items.length && <p className="mt-1 text-xs text-amber-700" data-testid={`bk-svc-staff-none-${line.key}`}>No eligible staff for this service — it can stay unassigned.</p>}
      {cur && !cur.available && <p className="mt-1 text-xs text-amber-700" data-testid={`bk-svc-staff-warn-${line.key}`}>{cur.name} has another booking or is unavailable at this time — the booking can't be saved with this overlap; choose another person or time.</p>}
    </div>
  );
}

export function ServiceStaffRows({ lines, date, time, branchId, value, onChange }) {
  let cur = dayjs(`${date}T${time}`);
  const starts = lines.map((l) => { const s = cur; cur = cur.add(l.duration, "minute"); return s; });
  const counts = lines.reduce((m, l) => (value[l.key] ? { ...m, [value[l.key]]: (m[value[l.key]] || 0) + 1 } : m), {});
  return (
    <div className="space-y-2 sm:col-span-2" data-testid="bk-svc-staff">
      <p className="text-xs font-semibold text-slate-600">Staff for each service</p>
      {lines.map((l, i) => <Row key={l.key} line={l} start={starts[i]} branchId={branchId} value={value[l.key]} onChange={(v) => onChange({ ...value, [l.key]: v })} />)}
      {Object.values(counts).some((n) => n > 1) && <p className="text-xs text-slate-500">One staff member has several services; they are scheduled one after another.</p>}
      <p className="text-[11px] text-slate-400">Availability uses staff working hours, leave and other bookings where these are set up.</p>
    </div>
  );
}
